import React, { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Package, ShoppingBag, LogOut, Sprout, ArrowLeft, Settings } from "lucide-react";
import AdminDashboardHome from "@/pages/admin/AdminDashboardHome";
import AdminOrdersPage from "@/pages/admin/AdminOrdersPage";
import AdminProductsPage from "@/pages/admin/AdminProductsPage";
import AdminSettingsPage from "@/pages/admin/AdminSettingsPage";

const AdminLayout = () => {
  const { t, adminToken, setAdminToken } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!adminToken) navigate("/admin/login");
  }, [adminToken, navigate]);

  if (!adminToken) return null;

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      isActive ? "bg-[#2D5A27] text-white" : "text-slate-300 hover:bg-[#2a3547] hover:text-white"
    }`;

  return (
    <div className="min-h-screen flex bg-[#F9F7F3]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex md:flex-col w-60 bg-[#1E293B] text-white p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-2 pb-4 border-b border-slate-700">
          <div className="w-9 h-9 rounded-lg bg-[#2D5A27] flex items-center justify-center">
            <Sprout className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-heading font-bold text-sm">Admin Panel</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider">Krishidut</p>
          </div>
        </div>
        <nav className="mt-4 flex flex-col gap-1 flex-1">
          <NavLink to="/admin" end className={linkCls} data-testid="admin-nav-dashboard">
            <LayoutDashboard className="w-4 h-4" /> {t("dashboard")}
          </NavLink>
          <NavLink to="/admin/orders" className={linkCls} data-testid="admin-nav-orders">
            <ShoppingBag className="w-4 h-4" /> {t("orders")}
          </NavLink>
          <NavLink to="/admin/products" className={linkCls} data-testid="admin-nav-products">
            <Package className="w-4 h-4" /> {t("manageProducts")}
          </NavLink>
          <NavLink to="/admin/settings" className={linkCls} data-testid="admin-nav-settings">
            <Settings className="w-4 h-4" /> {t("settings")}
          </NavLink>
        </nav>
        <div className="border-t border-slate-700 pt-3 space-y-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-[#2a3547]"
            data-testid="admin-back-to-site"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Site
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setAdminToken(null); navigate("/admin/login"); }}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-[#2a3547]"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#1E293B] text-white flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 rounded-md bg-[#2D5A27] flex items-center justify-center">
            <Sprout className="w-4 h-4 text-white" />
          </div>
          <p className="font-heading font-bold text-sm">Admin</p>
        </div>
        <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar" data-testid="admin-mobile-nav">
          <NavLink to="/admin" end className={({ isActive }) => `p-2 rounded-md ${isActive ? "bg-[#2D5A27]" : "hover:bg-[#2a3547]"}`} aria-label="Dashboard" data-testid="mobile-nav-dashboard">
            <LayoutDashboard className="w-4 h-4" />
          </NavLink>
          <NavLink to="/admin/orders" className={({ isActive }) => `p-2 rounded-md ${isActive ? "bg-[#2D5A27]" : "hover:bg-[#2a3547]"}`} aria-label="Orders" data-testid="mobile-nav-orders">
            <ShoppingBag className="w-4 h-4" />
          </NavLink>
          <NavLink to="/admin/products" className={({ isActive }) => `p-2 rounded-md ${isActive ? "bg-[#2D5A27]" : "hover:bg-[#2a3547]"}`} aria-label="Products" data-testid="mobile-nav-products">
            <Package className="w-4 h-4" />
          </NavLink>
          <NavLink to="/admin/settings" className={({ isActive }) => `p-2 rounded-md ${isActive ? "bg-[#2D5A27]" : "hover:bg-[#2a3547]"}`} aria-label="Settings" data-testid="mobile-nav-settings">
            <Settings className="w-4 h-4" />
          </NavLink>
          <button
            onClick={() => { setAdminToken(null); navigate("/admin/login"); }}
            className="p-2 rounded-md hover:bg-[#2a3547] ml-1"
            aria-label="Logout"
            data-testid="mobile-nav-logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </nav>
      </div>

      {/* Main */}
      <main className="flex-1 md:ml-0 mt-14 md:mt-0 p-4 md:p-8">
        <Routes>
          <Route index element={<AdminDashboardHome />} />
          <Route path="orders" element={<AdminOrdersPage />} />
          <Route path="products" element={<AdminProductsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminLayout;
